import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable } from 'rxjs';
import { User } from 'src/app/core/models/user.model';
import {
  ClaimsService,
  ClaimWithCase,
} from 'src/app/core/services/claims.service';
import { CoolingOffPeriodService } from 'src/app/core/services/cooling-off-period.service';
import { ModalViewControlService } from 'src/app/core/services/modal-view-control.service';
import { InactiveClaimModalComponent } from '../../shared/simple/inactive-claim-modal/inactive-claim-modal.component';
import { GuideCompletedModalComponent } from '../../shared/simple/guide-completed-modal/guide-completed-modal.component';
import { PostCompletionPromoModalComponent } from '../../shared/simple/post-completion-promo-modal/post-completion-promo-modal.component';

// Limite alto o suficiente para listar TODAS as claims ativas de um usuário no
// home (antes era 3, o que truncava quem tinha mais casos).
const HOME_CLAIMS_LIMIT = 50;

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
  claims?: ClaimWithCase[];
  user$!: Observable<User | null>;
  multiClaims: boolean = false;

  isLoading$ = new BehaviorSubject(true);

  constructor(
    private cs: ClaimsService,
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private modalViewControl: ModalViewControlService,
    private coolingOffPeriodService: CoolingOffPeriodService
  ) { }

  async ngOnInit() {
    console.log('[HOME] ngOnInit started');

    // Verifica se deve exibir o modal de guide completed (executa antes de carregar claims para garantir que o modal abra mesmo se houver erro na API)
    this.checkAndShowGuideCompletedModal();

    try {
      // Carrega TODAS as claims ativas (não-INACTIVE) do usuário. Antes o home
      // limitava a 3 e exibia apenas a primeira (claims[0]).
      const claims = await this.cs.getMyClaimsWithCase(HOME_CLAIMS_LIMIT, 0);

      console.log('[HOME] Claims received:', {
        count: claims?.length ?? 0,
        claimIds: claims?.map(c => c.id),
        statuses: claims?.map(c => c.status)
      });

      this.multiClaims = (claims?.length ?? 0) > 1;
      this.claims = claims;

      // Verifica se deve exibir o modal de claim inativa
      this.checkAndShowInactiveClaimModal(claims);
    } catch (error) {
      console.error('[HOME] Error loading claims:', error);
    } finally {
      this.isLoading$.next(false);
    }
  }

  private async checkAndShowGuideCompletedModal() {
    const guideCompleted = this.route.snapshot.queryParams['guideCompleted'] === 'true';

    if (!guideCompleted) {
      return;
    }

    try {
      const claims = await this.cs.getMyClaimsWithCase(1, 0);
      
      if (!claims?.length) {
        return;
      }

      const { data } = await this.cs.getCaseById(claims[0].case_id);
      const config: any = data?.configuration || {};
      const guideCompletedConfig = config?.guide_completed_modal;
      const promoConfig = config?.post_completion_promo;

      const targetUrl = (promoConfig?.targetCaseUrl || '').trim();

      const showGuideCompleted = !!guideCompletedConfig && guideCompletedConfig.enabled !== false;
      let showPromo = !!promoConfig && promoConfig.enabled !== false;
      if (showPromo && promoConfig?.consentKey) {
        const consentGiven = await this.cs.getConsentAnswer(claims[0].id, promoConfig.consentKey);
        showPromo = consentGiven;
      }

      const autoRedirect = !!targetUrl && showPromo;

      // Remove o query param antes de qualquer navegação posterior
      this.router.navigate([], {
        queryParams: {
          guideCompleted: null
        },
        queryParamsHandling: 'merge'
      });

      // Redirect automático só dispara depois que todos os pop-ups fecharem
      const runRedirect = () => {
        if (autoRedirect) {
          this.navigateToRedirect(targetUrl);
        }
      };

      if (showGuideCompleted) {
        const ref = this.dialog.open(GuideCompletedModalComponent, {
          width: '560px',
          disableClose: false,
          panelClass: 'guide-completed-modal',
          data: {
            guideCompletedConfig,
            caseId: claims[0].case_id,
            claimId: claims[0].id
          }
        });

        // Pop-up promocional é exibido após o cliente fechar o de "Cadastro completo"
        ref.afterClosed().subscribe(() => {
          if (showPromo) {
            this.openPromoModal(promoConfig).afterClosed().subscribe(() => runRedirect());
          } else {
            runRedirect();
          }
        });
      } else if (showPromo) {
        this.openPromoModal(promoConfig).afterClosed().subscribe(() => runRedirect());
      } else {
        runRedirect();
      }
    } catch (error) {
      console.error('[HOME] Error loading guide completed modal config:', error);
    }
  }

  private openPromoModal(promoConfig: any) {
    return this.dialog.open(PostCompletionPromoModalComponent, {
      width: '560px',
      disableClose: false,
      panelClass: 'post-completion-promo-modal',
      data: { promoConfig }
    });
  }

  private navigateToRedirect(url: string): void {
    // Rota interna do portal (ex.: "/client/cases/...") — externa começa com http(s)://
    if (url.startsWith('/') && !url.startsWith('//')) {
      this.router.navigateByUrl(url);
    } else {
      window.open(url, '_blank', 'noopener');
    }
  }

  private async checkAndShowInactiveClaimModal(claims?: ClaimWithCase[]) {
    // Verifica se veio do redirect por claim inativa
    const hasInactiveClaimParam = this.route.snapshot.queryParams['inactiveClaim'] === 'true';
    const inactiveClaimId = this.route.snapshot.queryParams['inactiveClaimId'] || null;
    const withdrawalType = this.route.snapshot.queryParams['withdrawalType'] || null; // 'cancellation' or 'termination'

    console.log('[HOME] checkAndShowInactiveClaimModal:', {
      hasInactiveClaimParam,
      inactiveClaimId,
      withdrawalType,
      claimsCount: claims?.length ?? 0
    });

    // Verifica se não há claims ativas
    const hasNoClaims = !claims || claims.length === 0;

    // Caminho 1: redirect explícito via query param (guard ou outra página)
    if (hasInactiveClaimParam) {
      // Defesa: se o usuário tem uma claim ACTIVE no mesmo case da inativa
      // (cenário de deduplicação / PoA do co-counsel), não mostra a modal.
      if (inactiveClaimId) {
        const inactiveClaim = await this.cs.getClaimWithCase(inactiveClaimId);
        if (inactiveClaim?.case_id) {
          const activeClaim = await this.cs.getMyActiveClaimForCase(inactiveClaim.case_id);
          if (activeClaim) {
            console.log('[HOME] Suppressing inactive claim modal — user has active claim for same case', {
              inactiveClaimId,
              activeClaimId: activeClaim.id,
            });
            this.router.navigate([], {
              queryParams: { inactiveClaim: null, inactiveClaimId: null, withdrawalType: null },
              queryParamsHandling: 'merge',
            });
            return;
          }
        }
      }

      console.log('[HOME] Opening modal due to inactiveClaim param');
      this.openInactiveClaimModal(inactiveClaimId, withdrawalType);
      return;
    }

    // Caminho 2: usuário sem claims ativas — verificar se tem alguma INACTIVE recente
    if (hasNoClaims) {
      const inactiveClaim = await this.cs.getMyLatestInactiveClaim();
      if (!inactiveClaim) {
        console.log('[HOME] No inactive claim found for user');
        return;
      }

      const inferredType = await this.inferWithdrawalType(inactiveClaim.id);
      console.log('[HOME] Auto-detected inactive claim, opening modal', {
        claimId: inactiveClaim.id,
        inferredType,
      });
      this.openInactiveClaimModal(inactiveClaim.id, inferredType);
    }
  }

  private async inferWithdrawalType(claimId: string): Promise<string | null> {
    try {
      const claim = await this.cs.getClaimWithCase(claimId);
      const coolingOff = await this.coolingOffPeriodService.getLatestForClaim(claim);

      if (!coolingOff.contractDate) return null;

      return coolingOff.isOver14Days ? 'termination' : 'cancellation';
    } catch (error) {
      console.error('[HOME] Error inferring withdrawal type:', error);
      return null;
    }
  }

  private openInactiveClaimModal(claimId?: string | null, withdrawalType: string | null = null) {
    console.log('[HOME] Opening inactive claim modal', { claimId, withdrawalType });

    const dialogRef = this.dialog.open(InactiveClaimModalComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'inactive-claim-modal',
      data: { claimId: claimId || undefined, withdrawalType }
    });

    dialogRef.afterClosed().subscribe(() => {
      // Marca como visto quando o modal é fechado
      this.modalViewControl.markModalAsViewed();
    });
  }

  handleMultipleClaims(multi: boolean) {
    if (multi) this.multiClaims = true;
  }
}
